// Browser-local persistence for UI/session state and pre-submit drafts.
//
// This module deliberately only touches `localStorage` inside the browser runtime and is a no-op
// (with graceful degradation) when `window` is unavailable or storage throws. It is scoped to the
// current browser profile only -- nothing here leaves the device, matching the orchestrator's
// 2026-08-15 HOLD partial lift: "bytes that stay in the user's own browser are GO".
//
// Key discipline:
// - All keys are prefixed with `aec-cde:`.
// - Submitted records live in the server-side shadow API (D1/R2). Drafts live here, in a
//   *separate* key namespace (`*:draft`), so a reload can never make a draft look like a
//   submitted issue.

import type { IssuePriority } from "./c1/shadowFoundation/types";

export type Page = "project" | "federation" | "documents" | "issues" | "rfis" | "approvals";

const STORAGE_KEY_PREFIX = "aec-cde:" as const;

const UI_STATE_KEY = `${STORAGE_KEY_PREFIX}ui-state` as const;
const ISSUE_DRAFT_KEY = `${STORAGE_KEY_PREFIX}issue-draft` as const;

export interface PersistedUiState {
  page: Page;
  projectId: string;
}

export interface PersistedIssueDraft {
  title: string;
  topicType: string;
  priority: IssuePriority;
  assigneeSubject: string;
  dueDate: string;
}

function storageAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function readJson<T>(key: string): T | null {
  if (!storageAvailable()) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson<T>(key: string, value: T | null): void {
  if (!storageAvailable()) return;
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // Silently ignore quota or private-mode errors. Persistence is a convenience, not authority.
  }
}

export function loadUiState(): PersistedUiState | null {
  return readJson<PersistedUiState>(UI_STATE_KEY);
}

export function saveUiState(state: PersistedUiState | null): void {
  writeJson(UI_STATE_KEY, state);
}

export function loadIssueDraft(): PersistedIssueDraft | null {
  return readJson<PersistedIssueDraft>(ISSUE_DRAFT_KEY);
}

export function saveIssueDraft(draft: PersistedIssueDraft | null): void {
  writeJson(ISSUE_DRAFT_KEY, draft);
}
